import './locales/index';
import { lazy, Suspense } from "react";
import { Outlet, Route, Routes } from "react-router";
import { LanguageProvider } from "@/components/global/LanguageToggle";
import { NotificationProvider } from "@/hooks/useNotification";
import { AuthProvider } from "@/features/auth/context/AuthProvider";
import { LoadingOverlay } from "@/components/ui/LoadingOverlay";
import { Sidebar } from "@/components/ui/Sidebar";
import { Header } from "@/components/ui/Header";
import { RequireAccess } from "@/components/auth/RequireAccess";
import { ROUTES } from "@/config/routes";
import { AccessLevel } from "@/config/access";
import { Footer } from "@/components/ui/Footer";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import { Toaster } from "@/components/_untitled/application/notifications/toaster";
import { AnalyticsProvider } from "@/components/global/AnalyticsProvider";
import { KycModalManager } from "@/components/global/KycModalManager";
import { UserNotificationsManager } from "@/components/global/UserNotificationsManager";
import { usePageTitle } from "@/hooks/usePageTitle";
import { FAQPage } from "@/pages/faq-page";

const AuthModalRedirect = lazy(() => import("@/features/auth/pages/AuthModalRedirect.tsx").then(m => ({ default: m.AuthModalRedirect })));
const LogoutRedirect = lazy(() => import("@/features/auth/pages/LogoutRedirect.tsx").then(m => ({ default: m.LogoutRedirect })));
const CallbackPage = lazy(() => import("@/features/auth/pages/CallbackPage.tsx").then(m => ({ default: m.CallbackPage })));
const ProfilePage = lazy(() => import("@/features/auth/pages/ProfilePage.tsx").then(m => ({ default: m.ProfilePage })));
const LandingPage = lazy(() => import("@/pages/landing-page.tsx").then(m => ({ default: m.LandingPage })));
const HomeScreen = lazy(() => import("@/pages/home-screen.tsx").then(m => ({ default: m.HomeScreen })));
const AuctionHousePage = lazy(() => import("@/pages/auction-house-page.tsx").then(m => ({ default: m.AuctionHousePage })));
const SalePage = lazy(() => import("@/pages/sale-page.tsx").then(m => ({ default: m.SalePage })));
const ItemPage = lazy(() => import("@/pages/item-page.tsx").then(m => ({ default: m.ItemPage })));
const PagesPage = lazy(() => import("@/pages/pages-page.tsx").then(m => ({ default: m.PagesPage })));
const AdminHomePage = lazy(() => import("@/pages/admin-home-page.tsx").then(m => ({ default: m.AdminHomePage })));
const AdminAssignAdminsPage = lazy(() => import("@/pages/admin-assign-admins-page.tsx").then(m => ({ default: m.AdminAssignAdminsPage })));
const AdminSaleImportsPage = lazy(() => import("@/pages/admin-sale-imports-page.tsx").then(m => ({ default: m.AdminSaleImportsPage })));
const AdminAuctionHousesPage = lazy(() => import("@/pages/admin-auction-houses-page.tsx").then(m => ({ default: m.AdminAuctionHousesPage })));
const ErrorDemoPage = lazy(() => import("@/pages/error-demo-page.tsx").then(m => ({ default: m.ErrorDemoPage })));
const CatalogListScreen = lazy(() => import("@/pages/catalog-list-screen.tsx").then(m => ({ default: m.CatalogListScreen })));
const CatalogScreen = lazy(() => import("@/pages/catalog-screen.tsx").then(m => ({ default: m.CatalogScreen })));
const ItemsScreen = lazy(() => import("@/pages/items-screen.tsx").then(m => ({ default: m.ItemsScreen })));
const NewItemScreen = lazy(() => import("@/pages/new-item-screen.tsx").then(m => ({ default: m.NewItemScreen })));
const LiveAuctionPage = lazy(() => import("@/pages/live-auction-page.tsx").then(m => ({ default: m.LiveAuctionPage })));
const InsightsHomePage = lazy(() => import("@/pages/insights-home-page.tsx").then(m => ({ default: m.InsightsHomePage })));
const InsightsUpgradePage = lazy(() => import("@/pages/insights-upgrade-page.tsx").then(m => ({ default: m.InsightsUpgradePage })));
const InsightsUnderbiddersPage = lazy(() => import("@/pages/insights-underbidders-page.tsx").then(m => ({ default: m.InsightsUnderbiddersPage })));
const InsightsTopRevenuePage = lazy(() => import("@/pages/insights-top-revenue-page.tsx").then(m => ({ default: m.InsightsTopRevenuePage })));
const CustomersPage = lazy(() => import("@/pages/customers-page.tsx").then(m => ({ default: m.CustomersPage })));
const AdminBiddersScreen = lazy(() => import("@/pages/admin-bidders-screen.tsx").then(m => ({ default: m.AdminBiddersScreen })));
const AuctionsPage = lazy(() => import("@/pages/auctions-page.tsx").then(m => ({ default: m.AuctionsPage })));
const EmailTemplatesPage = lazy(() => import("@/pages/email-templates-page.tsx").then(m => ({ default: m.EmailTemplatesPage })));
const EmailLogsPage = lazy(() => import("@/pages/email-logs-page.tsx").then(m => ({ default: m.EmailLogsPage })));
const ResetPasswordPage = lazy(() => import("@/pages/reset-password-page.tsx").then(m => ({ default: m.ResetPasswordPage })));
const NotFoundPage = lazy(() => import("@/pages/not-found-page.tsx").then(m => ({ default: m.NotFoundPage })));

function App() {
    usePageTitle();

    return (
        <ErrorBoundary>
            <LanguageProvider>
                <NotificationProvider>
                    <AuthProvider>
                        <AnalyticsProvider>
                            <UserNotificationsManager/>
                            <Toaster/>
                            <KycModalManager/>
                            <Suspense fallback={ <LoadingOverlay/> }>
                                <Routes>
                                {/* Public routes */}
                                <Route path={ ROUTES.LOGIN } element={ <AuthModalRedirect type="login"/> }/>
                                <Route path={ ROUTES.SIGN_UP } element={ <AuthModalRedirect type="signup"/> }/>
                                <Route path={ROUTES.LOGOUT} element={<LogoutRedirect />} />
                                <Route path={ROUTES.AUTH_CALLBACK} element={<CallbackPage />} />
                                <Route path={ROUTES.WELCOME} element={<ProfilePage />} />
                                <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />

                                {/* Live auction route - standalone layout */}
                                <Route path={ROUTES.LIVE_AUCTION} element={<LiveAuctionPage />} />

                                {/* Admin routes - all admin routes share Sidebar layout */}
                                <Route path="/admin" element={
                                    <RequireAccess level={AccessLevel.AUCTION_HOUSE_STAFF} redirectTo={ROUTES.LOGIN}>
                                        <div className="flex w-full h-full flex-col lg:flex-row">
                                            <Sidebar/>
                                            <main className="flex-1 overflow-auto px-4">
                                                <Outlet/>
                                            </main>
                                        </div>
                                    </RequireAccess>
                                }>
                                    {/* Admin home */}
                                    <Route index element={<AdminHomePage />} />

                                    {/* System Admin only */}
                                    <Route path="error-demo" element={
                                        <RequireAccess route={ROUTES.ADMIN_ERROR_DEMO} redirectTo={ROUTES.HOME}>
                                            <ErrorDemoPage />
                                        </RequireAccess>
                                    } />

                                    <Route path="assign-admins" element={
                                        <RequireAccess route={ROUTES.ADMIN_ASSIGN_ADMINS} redirectTo={ROUTES.HOME}>
                                            <AdminAssignAdminsPage />
                                        </RequireAccess>
                                    } />

                                    <Route path="sale-imports" element={
                                        <RequireAccess route={ROUTES.ADMIN_SALE_IMPORTS} redirectTo={ROUTES.HOME}>
                                            <AdminSaleImportsPage />
                                        </RequireAccess>
                                    } />

                                    <Route path="auction-houses" element={
                                        <RequireAccess route={ROUTES.ADMIN_AUCTION_HOUSES} redirectTo={ROUTES.HOME}>
                                            <AdminAuctionHousesPage />
                                        </RequireAccess>
                                    } />

                                    <Route path="email-templates" element={
                                        <RequireAccess level={AccessLevel.SYSTEM_ADMIN} redirectTo={ROUTES.HOME}>
                                            <EmailTemplatesPage />
                                        </RequireAccess>
                                    } />

                                    <Route path="email-logs" element={
                                        <RequireAccess level={AccessLevel.SYSTEM_ADMIN} redirectTo={ROUTES.HOME}>
                                            <EmailLogsPage />
                                        </RequireAccess>
                                    } />

                                    {/* User Settings */}
                                    <Route path="settings" element={<ProfilePage />} />

                                    {/* Auction house routes - protected by parent RequireAccess */}
                                    <Route path=":slug">
                                        <Route index element={<CatalogListScreen />} />
                                        <Route path="new" element={<div>New Catalog Form - TODO</div>} />
                                        <Route path=":catalogId" element={<CatalogScreen />} />
                                        <Route path=":catalogId/:lotNumber" element={ <NewItemScreen/> }/>
                                        <Route path="items" element={<ItemsScreen />} />
                                        <Route path="items/:itemId" element={<NewItemScreen />} />
                                        <Route path="insights" element={<InsightsHomePage />} />
                                        <Route path="insights/upgrade" element={<InsightsUpgradePage />} />
                                        <Route path="insights/underbidders" element={<InsightsUnderbiddersPage />} />
                                        <Route path="insights/top-revenue" element={<InsightsTopRevenuePage />} />
                                        <Route path="customers" element={<CustomersPage />} />
                                        <Route path="bidders" element={ <AdminBiddersScreen/> }/>
                                    </Route>
                                </Route>

                                {/* Public pages with marketing header/footer */}
                                <Route path="/" element={
                                    <div className="flex flex-col min-h-screen w-full">
                                        <Header />
                                        <main className="flex-1"><Outlet /></main>
                                        <Footer />
                                    </div>
                                }>
                                    {/* Home page with items */}
                                    <Route index element={<HomeScreen />} />
                                    {/* Landing page */}
                                    <Route path="home" element={<LandingPage />} />
                                    {/* Auctions page */}
                                    <Route path="auctions" element={<AuctionsPage />} />
                                    {/* User Settings */}
                                    <Route path="settings" element={<ProfilePage />} />
                                    {/* Static pages */ }
                                    <Route path="pages/faq" element={ <FAQPage/> }/>
                                    <Route path="pages/:pageId" element={ <PagesPage/> }/>
                                    {/* Public auction routes - hierarchical structure */ }
                                    <Route path=":slug" element={ <AuctionHousePage/> }/>
                                    <Route path=":slug/:catalogId" element={ <SalePage/> }/>
                                    <Route path=":slug/:catalogId/:lotNumber" element={ <ItemPage/> }/>
                                    {/* Explicit 404 route for redirects */ }
                                    <Route path="404" element={ <NotFoundPage/> }/>
                                </Route>

                                {/* 404 catch-all route - must be last */ }
                                <Route path="*" element={ <NotFoundPage/> }/>
                            </Routes>
                        </Suspense>
                        </AnalyticsProvider>
                    </AuthProvider>
                </NotificationProvider>
            </LanguageProvider>
        </ErrorBoundary>
    );
}

export default App;